import { Button, CircularProgress, Text, Tooltip } from '@shared/components';
import { useUpdateQuoteRateMutations } from '@shared/components/money-form/hooks/useUpdateQuoteRateMutations';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { CarrierOffers } from '@shared/features/carrier-offers/CarrierOffers';
import { LoadboardDetails } from '@shared/features/loadboard-details/LoadboardDetails';
import {
  PricingForm,
  PricingFormValues,
} from '@shared/features/loadboard-details/PricingForm';
import { NegotiationStrategyOverride } from '@shared/features/shipment-card/VoomaCover/NegotiationStrategyOverride';
import { countLoadBoardIntegrations } from '@shared/features/shipment-card/VoomaCover/VoomaCover';
import { SuggestedCarriers } from '@shared/features/suggested-carriers/SuggestedCarriers';
import {
  CustomerRateType,
  useCreateCustomerRateMutation,
} from '@shared/generated/graphql';
import useMovement from '@shared/graphql/hooks/movements/useMovement';
import { useEnsureShipmentExistsForQuote } from '@shared/hooks/shipments/useEnsureShipmentExistsForQuote';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { useFeatureFlagEnabled } from '@shared/hooks/useFeatureFlagEnabled';
import { CarrierRate, Quote } from '@shared/types/quote';
import { Shipment } from '@shared/types/shipment';
import { getConfiguredIntegrations } from '@shared/utils/userFeatures/getConfiguredIntegrations';
import { FeatureFlagName } from 'clerk_common/types/featureFlags';
import { useState } from 'react';
import { LuExternalLink, LuMinus, LuPlus } from 'react-icons/lu';

type CoverQuoteProps = {
  quote: Quote;
  openShipment?: (shipmentId?: string) => void;
};

export const CoverQuote = (p: CoverQuoteProps) => {
  const [expanded, setExpanded] = useState(false);
  const { track } = useAnalytics();

  const handleToggleHistory = () => {
    const event = expanded
      ? 'Collapse Vooma Cover for quote'
      : 'Expand Vooma Cover for quote';
    setExpanded(!expanded);
    track(event, {
      quoteId: p.quote.id,
    });
  };

  return (
    <div className="flex flex-col p-4">
      <>
        <div
          onClick={handleToggleHistory}
          className="flex cursor-pointer flex-row items-center justify-between text-gray-600"
        >
          <div className="flex flex-row items-center gap-6">
            <Text type="body-md">Contact Carriers</Text>
            {p.quote.shipmentId && p.openShipment && (
              <Button
                variant="secondary"
                size="xs"
                icon={<LuExternalLink />}
                onPress={() => p.openShipment?.(p.quote.shipmentId)}
              >
                Ops view
              </Button>
            )}
          </div>
          <Tooltip
            isInstant
            content={
              expanded ? 'Collapse contact carriers' : 'Expand contact carriers'
            }
          >
            {expanded ? <LuMinus size={16} /> : <LuPlus size={16} />}
          </Tooltip>
        </div>
        {expanded && <QuoteToShipmentForCover quoteId={p.quote.id} />}
      </>
    </div>
  );
};

function QuoteToShipmentForCover({ quoteId }: { quoteId: string }) {
  const { shipment, isLoading } = useEnsureShipmentExistsForQuote({
    quoteId,
  });
  const { handleSetQuoteCarrierRate, handleSetQuoteCustomerRate } =
    useUpdateQuoteRateMutations();
  const [createCustomerRate] = useCreateCustomerRateMutation();

  if (isLoading) {
    return (
      <div className="mt-2 flex flex-col items-center gap-2">
        <CircularProgress size="xs" />
      </div>
    );
  }
  if (!shipment) {
    return (
      <div className="mt-2 flex flex-col gap-2 text-gray-600">
        <Text type="body-sm">No shipment found for quote</Text>
      </div>
    );
  }

  const setCarrierRate = async (rateId: string) => {
    await handleSetQuoteCarrierRate(quoteId, rateId);
    const res = await createCustomerRate({
      variables: {
        input: { quoteId, type: CustomerRateType.QUOTED },
      },
    });
    const customerRateId = res.data?.createCustomerRate.customerRate.id;
    if (customerRateId)
      await handleSetQuoteCustomerRate(quoteId, customerRateId);
  };

  return (
    <CoverQuoteShipment shipment={shipment} setCarrierRate={setCarrierRate} />
  );
}

function CoverQuoteShipment({
  shipment,
  setCarrierRate,
}: {
  shipment: Shipment;
  setCarrierRate: (rateId: string) => Promise<void>;
}) {
  const movement = shipment.movementPreviews[0];

  if (!movement) {
    return (
      <div className="mt-2 flex flex-col gap-2">
        <Text type="body-sm">No movement found for shipment</Text>
      </div>
    );
  }

  return (
    <CoverQuoteMovement
      shipmentId={shipment.id}
      movementId={movement.id}
      setCarrierRate={setCarrierRate}
    />
  );
}

function CoverQuoteMovement({
  shipmentId,
  movementId,
  setCarrierRate,
}: {
  shipmentId: string;
  movementId: string;
  setCarrierRate: (rateId: string) => Promise<void>;
}) {
  const { me, defaultOrgId } = useMeContext();
  const configuredIntegrations = getConfiguredIntegrations(
    me,
    defaultOrgId ?? ''
  );

  const { movement, updateMovement, isLoading, updateIsLoading } = useMovement({
    id: movementId,
  });
  const { isEnabled: showNegotiationStrategyOverride } = useFeatureFlagEnabled({
    name: FeatureFlagName.SHOW_NEGOTIATION_STRATEGY_OVERRIDE,
    organizationId: defaultOrgId,
  });
  const pricingData = movement?.pricing;

  const updatePricing = (data: PricingFormValues) => {
    updateMovement({
      id: movementId,
      pricing: {
        targetPrice: data.targetPrice
          ? parseFloat(data.targetPrice)
          : undefined,
        maxPrice: data.maxPrice ? parseFloat(data.maxPrice) : undefined,
      },
    });
  };

  const showLoadboardDetails =
    countLoadBoardIntegrations(configuredIntegrations) > 0;
  const showSuggestedCarriers = true;

  const onSelectCarrierOffer = async (offer: CarrierRate) => {
    await setCarrierRate(offer.id);
  };

  return (
    <div className="mt-4 flex flex-col gap-4">
      <PricingForm
        isLoading={isLoading || updateIsLoading}
        onSubmit={updatePricing}
        recordId={movementId}
        initialFormData={{
          targetPrice: pricingData?.targetPrice?.toString(),
          maxPrice: pricingData?.maxPrice?.toString(),
        }}
      />
      {showNegotiationStrategyOverride && (
        <NegotiationStrategyOverride
          movementId={movementId}
          currentOverride={movement?.negotiationStrategyConfigOverride}
          updateMovement={updateMovement}
          isLoading={isLoading || updateIsLoading}
        />
      )}
      <CarrierOffers
        movementId={movementId}
        carrierOfferActions={[
          { label: 'Set as buy rate', onPress: onSelectCarrierOffer },
        ]}
      />
      {showLoadboardDetails && <LoadboardDetails shipmentId={shipmentId} />}
      {showSuggestedCarriers && <SuggestedCarriers shipmentId={shipmentId} />}
    </div>
  );
}
